/**
 * AuthContext — глобальное состояние авторизации сайта.
 *
 * Держит: текущего пользователя (userId, username), баланс алмазов,
 * флаги загрузки. Восстанавливает сессию из localStorage при старте.
 *
 * Использование:
 *   const { user, login, signup, logout, diamonds } = useAuth();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
/* Через шлюз, а не напрямую в api/auth: он выбирает между user-сервисом v1
   и identity v2 по переменной REACT_APP_IDENTITY и приводит оба ответа
   к одной форме. Контекст не должен знать, какая версия активна —
   иначе переключение расползлось бы по всему приложению. */
import * as authApi from "../api/authGateway";
import { AUTH_LOST_EVENT, ensureFreshAccess } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user = null, пока не залогинен; {userId, username, token} — когда залогинен
  const [user, setUser] = useState(null);
  const [diamonds, setDiamonds] = useState(null);
  // initializing — пока проверяем localStorage на старте (избегаем мигания UI)
  const [initializing, setInitializing] = useState(true);
  // sessionExpired — сессия оборвалась во время работы (не при старте).
  // Нужен, чтобы показать «Войди заново» тому, кто ТОЛЬКО ЧТО работал,
  // и не дёргать этим гостя, который и не был залогинен.
  const [sessionExpired, setSessionExpired] = useState(false);

  const diamondAbortRef = useRef(null);

  /* ── подгрузка алмазов залогиненного пользователя ──────────────── */
  const refreshDiamonds = useCallback(async () => {
    if (diamondAbortRef.current) diamondAbortRef.current.abort();
    const ctrl = new AbortController();
    diamondAbortRef.current = ctrl;
    try {
      const count = await authApi.getDiamonds(ctrl.signal);
      setDiamonds(count);
    } catch (e) {
      if (e.name !== "AbortError") setDiamonds(null);
    }
  }, []);

  /* ── потеря сессии ─────────────────────────────────────────────────
   * HTTP-слой бросает событие, только когда сервер ЯВНО отверг refresh
   * (401/403). При 5xx и обрывах сети события не будет — пользователь
   * останется залогиненным и попробует снова при следующем действии. */
  useEffect(() => {
    const onAuthLost = () => {
      setUser(null);
      setDiamonds(null);
      setSessionExpired(true);
    };
    window.addEventListener(AUTH_LOST_EVENT, onAuthLost);
    return () => window.removeEventListener(AUTH_LOST_EVENT, onAuthLost);
  }, []);

  /* ── восстановление сессии при загрузке сайта ──────────────────── */
  useEffect(() => {
    let cancelled = false;
    // Асинхронно: истёкший access продлеваем по refresh, а не выкидываем
    // пользователя (access живёт 24 часа, refresh — 30 дней).
    authApi.restoreSessionAsync().then((session) => {
      if (cancelled) return;
      if (!session) {
        setInitializing(false);
        return;
      }
      // токен валиден — показываем пользователя сразу, имя/фото догружаем
      setUser(session);
      authApi
        .getProfile()
        .then((profile) => {
          if (cancelled) return;
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  username: profile.firstName || prev.username || null,
                  photo: profile.photo || prev.photo || null,
                }
              : prev
          );
        })
        .catch(() => {
          /* профиль не отдался — остаёмся с userId без имени */
        })
        .finally(() => {
          if (!cancelled) setInitializing(false);
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ── проактивное продление сессии ───────────────────────────────────
   * Не ждём 401 посреди действия пользователя, а обновляем токен заранее:
   * при возврате на вкладку и раз в 30 минут (access живёт 24 часа).
   * Это же чинит «вернулся к вкладке через день — всё отвалилось». */
  useEffect(() => {
    if (!user) return undefined;

    const tick = () => {
      if (document.visibilityState === "visible") {
        ensureFreshAccess().catch(() => {});
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };

    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(tick, 30 * 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, [user]);

  // Вошли заново (любым способом: пароль, OAuth, SSO-тикет) — уведомление
  // об истёкшей сессии больше не актуально.
  useEffect(() => {
    if (user) setSessionExpired(false);
  }, [user]);

  // когда пользователь появился — тянем баланс алмазов
  useEffect(() => {
    if (user) refreshDiamonds();
    else setDiamonds(null);
  }, [user, refreshDiamonds]);

  /* ── действия ──────────────────────────────────────────────────── */

  const login = useCallback(async ({ email, password }) => {
    const result = await authApi.login({ email, password });
    setUser({
      token: result.token,
      userId: result.userId,
      username: result.username,
      photo: result.photo || null,
    });
    return result;
  }, []);

  /** OAuth-вход (Yandex и т.п.). Принимает code+state из callback URL,
   *  обменивает их на наш JWT через бэк и устанавливает user. */
  const loginWithOAuth = useCallback(async ({ provider, code, state }) => {
    let result;
    if (provider === "yandex") {
      result = await authApi.exchangeYandexCode({ code, state });
    } else if (provider === "google") {
      result = await authApi.exchangeGoogleCode({ code, state });
    } else {
      throw new Error("Неизвестный OAuth-провайдер");
    }
    setUser({
      token: result.token,
      userId: result.userId,
      username: result.username,
      photo: result.photo || null,
    });
    return result;
  }, []);

  const signup = useCallback(
    async ({
      email, password, firstName, birthDate, gender, website,
      captchaIdLeft, captchaAnswerLeft,
      captchaIdRight, captchaAnswerRight,
      canvasData,
    }) => {
      const result = await authApi.signupAndLogin({
        email,
        password,
        firstName,
        birthDate,
        gender,
        website,
        captchaIdLeft,
        captchaAnswerLeft,
        captchaIdRight,
        captchaAnswerRight,
        canvasData,
      });
      setUser({
        token: result.token,
        userId: result.userId,
        username: result.username,
        photo: result.photo || null,
      });
      return result;
    },
    []
  );

  const logout = useCallback(() => {
    authApi.logout();
    setUser(null);
    setDiamonds(null);
  }, []);

  /** Закрыть уведомление об истёкшей сессии (пользователь его увидел). */
  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);

  const value = {
    user,
    isAuthenticated: !!user,
    initializing,
    sessionExpired,
    dismissSessionExpired,
    diamonds,
    refreshDiamonds,
    login,
    loginWithOAuth,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Хук доступа к состоянию авторизации. */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth должен использоваться внутри <AuthProvider>");
  }
  return ctx;
}
